import { useTranslation } from "react-i18next";
import { ArrowRight, CheckCircle2, Users } from "lucide-react";
import { CountUp } from "../ui/CountUp";
import { HeroMockup } from "./HeroMockup";
import { PillButton, ScribbleUnderline, StickerBadge } from "../ui/decorations";

interface LandingHeroProps {
  onLoginClick: () => void;
}

/** Hero da landing: copy + CTAs à esquerda, mockup do app à direita. */
export function LandingHero({ onLoginClick }: LandingHeroProps) {
  const { t } = useTranslation("landing");
  return (
    <section className="relative overflow-hidden bg-cream pt-32 pb-20 md:pt-40 md:pb-28">
      <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-[55fr_45fr] gap-16 lg:gap-10 items-center">
        {/* Copy */}
        <div className="text-center lg:text-left">
          <a href="#colaboracao" className="inline-block">
            <StickerBadge className="border-ink/15 bg-white text-ink mb-6 hover:border-primary-300 transition-colors cursor-pointer">
              <Users size={12} className="text-primary-600" />
              {t("hero.badge")}
            </StickerBadge>
          </a>

          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl xl:text-7xl font-extrabold text-ink tracking-tight leading-[1.05] text-balance">
            {t("hero.titlePrefix")}{" "}
            <ScribbleUnderline>{t("hero.titleHighlight")}</ScribbleUnderline>
            {t("hero.titleSuffix")}
          </h1>

          <p className="mt-6 text-base sm:text-lg text-ink/70 leading-relaxed max-w-xl mx-auto lg:mx-0">
            {t("hero.subtitle")}
          </p>

          {/* CTAs */}
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start items-center">
            <PillButton
              id="hero-cta-btn"
              onClick={onLoginClick}
              hardShadow="primary"
              className="w-full sm:w-auto px-8 py-4 bg-ink text-cream text-base"
            >
              {t("hero.ctaStart")}
              <ArrowRight size={17} />
            </PillButton>
            <a
              href="#features"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-4 text-ink/70 hover:text-ink font-semibold text-sm rounded-full border-2 border-ink/15 hover:border-ink/30 transition-all duration-200"
            >
              {t("hero.ctaHow")}
            </a>
          </div>

          {/* Social proof */}
          <div className="mt-10 flex flex-wrap justify-center lg:justify-start items-center gap-x-6 gap-y-3 text-xs font-semibold text-ink/60">
            <span className="flex items-center gap-1.5">
              <CheckCircle2 size={13} className="text-primary-600" />
              {t("hero.proofNoCard")}
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle2 size={13} className="text-primary-600" />
              <CountUp end={10} /> {t("hero.proofNotes")}
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle2 size={13} className="text-primary-600" />
              <CountUp end={100} suffix=" MB" /> {t("hero.proofStorage")}
            </span>
          </div>
        </div>

        {/* Mockup */}
        <div className="px-6 sm:px-10 lg:px-0 lg:pr-4">
          <HeroMockup />
        </div>
      </div>
    </section>
  );
}
